import { useState, useEffect, useCallback } from 'react';
import { GiftRecord, GiftStatistics, RSVPResponse } from '../../../types/invitation';
import { giftService, rsvpService } from '../../../lib/supabase';
import { createLogger } from '../../../lib/logger';

const log = createLogger('gift');

const INITIAL_STATS: GiftStatistics = {
  totalAmount: 0,
  totalRecords: 0,
  averageAmount: 0,
  guestSideStats: {
    bride: { totalAmount: 0, count: 0 },
    groom: { totalAmount: 0, count: 0 },
  },
  currencyBreakdown: {},
  giftTypeBreakdown: {
    cash: { count: 0, total: 0, percentage: 0 },
    gift: { count: 0, total: 0, percentage: 0 },
    both: { count: 0, total: 0, percentage: 0 },
  },
};

export function useGiftData(
  invitationId: string,
  inviteCode: string,
  userId: string | undefined,
  refreshTrigger?: number
) {
  const [giftRecords, setGiftRecords] = useState<GiftRecord[]>([]);
  const [giftStats, setGiftStats] = useState<GiftStatistics>(INITIAL_STATS);
  const [rsvpResponses, setRSVPResponses] = useState<RSVPResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);

      const [records, stats, rsvps] = await Promise.all([
        giftService.getGiftRecordsByInvitation(invitationId),
        giftService.getGiftStatistics(invitationId),
        rsvpService.getRSVPsByInviteCode(inviteCode, userId),
      ]);

      setGiftRecords(records);
      setGiftStats(stats);
      setRSVPResponses(rsvps.filter(r => r.rsvpData.attending === 'yes'));
    } catch (error) {
      log.error('載入紅包資料失敗:', error);
      alert(`載入失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setLoading(false);
    }
  }, [invitationId, inviteCode, userId]);

  useEffect(() => {
    loadData();
  }, [invitationId, inviteCode, userId, refreshTrigger, loadData]);

  return {
    giftRecords,
    giftStats,
    rsvpResponses,
    loading,
    refresh: loadData,
  };
}
